import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { api, asset } from "../api";
import { Layout } from "../components/Layout";
import { JsonLd, Meta } from "../components/JsonLd";
import { useReveal, gsap, ScrollTrigger } from "../useReveal";

/* ---------------- Zaman Tüneli ---------------- */
function ZamanTuneli({ slug }) {
  const { data: kareler = [] } = useQuery({ queryKey: ["timeline", slug], queryFn: async () => (await api.get(`/projects/${slug}/timeline`)).data });
  const [idx, setIdx] = useState(0);
  const sarici = useRef(null);
  const reduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    if (reduce || kareler.length < 2 || window.innerWidth <= 860) return;
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: sarici.current, start: "top top", end: "bottom bottom", scrub: true,
        onUpdate: (self) => {
          const i = Math.min(kareler.length - 1, Math.floor(self.progress * kareler.length));
          setIdx(i);
        },
      });
    });
    const t = setTimeout(() => ScrollTrigger.refresh(), 200);
    return () => { clearTimeout(t); ctx.revert(); };
  }, [kareler, reduce]);
  if (kareler.length === 0) return null;
  const aktif = kareler[idx] || kareler[0];

  // prefers-reduced-motion: normal galeri olarak davran
  if (reduce) {
    return (
      <section id="ilerleme">
        <div className="bolum-basi"><h2 className="h-orta bel">İnşaat zaman tüneli</h2><div className="veri bel">Aynı açı · aylık kayıt</div></div>
        <div className="tunel-mobil" style={{ display: "flex" }} data-testid="tunel-galeri">
          {kareler.map((k) => (
            <div className="kare" key={k.id}>
              <img src={asset(k.foto)} alt={k.label} loading="lazy" />
              <div className="alt"><div className="tunel-ay">{k.label} · %{k.yuzde}</div><div className="tunel-not">{k.not_}</div></div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section id="ilerleme">
      <div className="bolum-basi"><h2 className="h-orta bel">İnşaat zaman tüneli</h2><div className="veri bel">Aynı açı · aylık kayıt</div></div>
      <div ref={sarici} style={{ height: window.innerWidth > 860 ? `${kareler.length * 80}vh` : "auto" }}>
        <div style={{ position: window.innerWidth > 860 ? "sticky" : "static", top: 90 }}>
          <div className="tunel">
            <div className="tunel-sol">
              <div className="tunel-yuzde">%{aktif.yuzde}</div>
              <div className="tunel-ay">{aktif.label}</div>
              <div className="tunel-not">{aktif.not_}</div>
              <input className="tunel-slider" type="range" min={0} max={kareler.length - 1} value={idx} onChange={(e) => setIdx(Number(e.target.value))} data-testid="tunel-slider" aria-label="Zaman tüneli" />
            </div>
            <div className="tunel-sag">
              {kareler.map((k, i) => (<img key={k.id} src={asset(k.foto)} alt={k.label} className={i === idx ? "aktif" : ""} loading="lazy" />))}
            </div>
          </div>
          <div className="tunel-mobil">
            {kareler.map((k) => (
              <div className="kare" key={k.id}>
                <img src={asset(k.foto)} alt={k.label} loading="lazy" />
                <div className="alt"><div className="tunel-ay">{k.label} · %{k.yuzde}</div><div className="tunel-not">{k.not_}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Kat Manzara ---------------- */
function KatManzara({ slug }) {
  const { data: katlar = [] } = useQuery({ queryKey: ["floors", slug], queryFn: async () => (await api.get(`/projects/${slug}/floor-views`)).data });
  const [aktif, setAktif] = useState(null);
  useEffect(() => { if (katlar.length && aktif === null) setAktif(katlar[katlar.length - 1].kat); }, [katlar, aktif]);
  const okTus = (e) => {
    if (!katlar.length) return;
    const idx = katlar.findIndex((k) => k.kat === aktif);
    if (e.key === "ArrowUp" && idx < katlar.length - 1) { e.preventDefault(); setAktif(katlar[idx + 1].kat); }
    if (e.key === "ArrowDown" && idx > 0) { e.preventDefault(); setAktif(katlar[idx - 1].kat); }
  };
  if (katlar.length === 0) return null;
  const secili = katlar.find((k) => k.kat === aktif) || katlar[0];
  // fallback: en yakın fotoğrafı olan kat
  const foto = secili.foto || (() => {
    const withFoto = katlar.filter((k) => k.foto);
    if (!withFoto.length) return null;
    withFoto.sort((a, b) => Math.abs(a.kat - secili.kat) - Math.abs(b.kat - secili.kat));
    return withFoto[0].foto;
  })();

  return (
    <section id="manzara">
      <div className="bolum-basi"><h2 className="h-orta bel">Kat yükseldikçe manzara</h2><div className="veri bel">Kotu seçin</div></div>
      <div className="manzara bel">
        <div className="manzara-kat" data-testid="manzara-katlar" role="listbox" tabIndex={0} aria-label="Kat seçimi (ok tuşları)" onKeyDown={okTus}>
          {katlar.map((k) => (
            <button key={k.id} className={k.kat === aktif ? "aktif" : ""} onClick={() => setAktif(k.kat)} data-testid={`manzara-kat-${k.kat}`}>
              <span>{k.kat}. kat</span><span>{k.kot}</span>
            </button>
          ))}
        </div>
        <div className="manzara-gorsel">
          <div className="manzara-kot">{secili.kot}{!secili.foto && " · en yakın kat"}</div>
          {foto ? <img src={asset(foto)} alt={`${secili.kat}. kat manzara`} /> : <div style={{ display: "grid", placeItems: "center", height: "100%" }} className="veri">Bu kat için görsel yok</div>}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Daire Seçimi (SVG poligon, 3 kademe) ---------------- */
function pointsStr(points) {
  return (points || []).map((p) => `${p[0]},${p[1]}`).join(" ");
}
function bbox(points) {
  const xs = points.map((p) => p[0]), ys = points.map((p) => p[1]);
  return { x1: Math.min(...xs), y1: Math.min(...ys), x2: Math.max(...xs), y2: Math.max(...ys) };
}
const DURUM_METIN = { musait: "Müsait", opsiyonlu: "Opsiyonlu", satildi: "Satıldı" };

function DaireSecim({ slug, p }) {
  const { data: daireler = [] } = useQuery({ queryKey: ["apartments", slug], queryFn: async () => (await api.get(`/projects/${slug}/apartments`)).data });
  const [kademe, setKademe] = useState("blok");
  const [seciliBlok, setSeciliBlok] = useState(null);
  const [seciliKat, setSeciliKat] = useState(null);
  const [f, setF] = useState({ oda: "", cephe: "", sadeceMusait: false, maxFiyat: "", maxM2: "" });
  const [secili, setSecili] = useState(null);
  const [karsilastir, setKarsilastir] = useState([]);
  const [hoverBlok, setHoverBlok] = useState(null);
  const odalar = useMemo(() => [...new Set(daireler.map((d) => d.oda))], [daireler]);
  const cepheler = useMemo(() => [...new Set(daireler.map((d) => d.cephe))], [daireler]);
  const bloklar = useMemo(() => [...new Set(daireler.map((d) => d.blok))].sort(), [daireler]);

  const uygun = (d) => (!f.oda || d.oda === f.oda) && (!f.cephe || d.cephe === f.cephe) && (!f.sadeceMusait || d.durum === "musait") && (!f.maxFiyat || d.fiyat <= Number(f.maxFiyat)) && (!f.maxM2 || d.m2 <= Number(f.maxM2));
  const blokMusait = (b) => daireler.filter((d) => d.blok === b && d.durum === "musait").length;
  const karsilastirEkle = (d) => setKarsilastir((k) => (k.find((x) => x.id === d.id) ? k : k.length >= 3 ? k : [...k, d]));

  if (daireler.length === 0) return null;

  const blokPoly = p?.blok_polygons || [];
  const seciBlok = (b) => { setSeciliBlok(b); setKademe("kat"); };
  const seciKat = (k) => { setSeciliKat(k); setKademe("daire"); };
  const katlar = seciliBlok ? [...new Set(daireler.filter((d) => d.blok === seciliBlok).map((d) => d.kat))].sort((a, b) => b - a) : [];
  const floorplan = (p?.floorplans || []).find((fp) => fp.blok === seciliBlok && String(fp.kat) === String(seciliKat));
  const katDaireleri = daireler.filter((d) => d.blok === seciliBlok && String(d.kat) === String(seciliKat));

  return (
    <section id="daireler">
      <div className="bolum-basi"><h2 className="h-orta bel">Daire seçimi</h2><div className="veri bel">{daireler.filter((d) => d.durum === "musait").length} müsait daire</div></div>

      {/* breadcrumb */}
      <div className="daire-yol veri bel">
        <button className={kademe === "blok" ? "aktif" : ""} onClick={() => setKademe("blok")} data-testid="kademe-blok">Blok</button>
        {seciliBlok && <><span>›</span><button className={kademe === "kat" ? "aktif" : ""} onClick={() => setKademe("kat")} data-testid="kademe-kat">{seciliBlok} Blok</button></>}
        {kademe === "daire" && seciliKat != null && <><span>›</span><span className="aktif">{seciliKat}. kat</span></>}
      </div>

      {/* KADEME 1: BLOK */}
      {kademe === "blok" && (
        <div className="bel" data-testid="kademe-blok-panel">
          {p?.blok_render && blokPoly.length > 0 ? (
            <div className="poligon-sarici" onMouseLeave={() => setHoverBlok(null)}>
              <img src={asset(p.blok_render)} alt="Blok yerleşimi" />
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="poligon-svg">
                {blokPoly.map((pg) => (
                  <polygon key={pg.blok} points={pointsStr(pg.points)} className="pg-blok"
                    tabIndex={0} role="button" aria-label={`${pg.blok} blok, ${blokMusait(pg.blok)} müsait daire`}
                    onMouseEnter={() => setHoverBlok(pg.blok)} onClick={() => seciBlok(pg.blok)}
                    onKeyDown={(e) => e.key === "Enter" && seciBlok(pg.blok)} data-testid={`poly-blok-${pg.blok}`} />
                ))}
              </svg>
              {hoverBlok && <div className="poligon-ipucu veri" data-testid="poligon-ipucu">{hoverBlok} Blok · {blokMusait(hoverBlok)} müsait</div>}
            </div>
          ) : (
            <div className="daire-kutular">
              {bloklar.map((b) => (
                <button key={b} className="daire-kutu" onClick={() => seciBlok(b)} data-testid={`blok-${b}`}>
                  <b>{b} Blok</b><span className="veri">{blokMusait(b)} müsait daire</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* KADEME 2: KAT (bloğun kesiti — yatay şeritler) */}
      {kademe === "kat" && (
        <div className="kat-kesit bel" data-testid="kademe-kat-panel">
          {katlar.map((k) => {
            const list = daireler.filter((d) => d.blok === seciliBlok && String(d.kat) === String(k));
            const sold = list.filter((d) => d.durum === "satildi").length;
            const occ = list.length ? sold / list.length : 0;
            const kot = list[0]?.kot;
            return (
              <button key={k} className="kat-serit" style={{ background: `rgba(185,143,85,${(0.08 + occ * 0.55).toFixed(3)})` }}
                onClick={() => seciKat(k)} data-testid={`kat-${k}`}>
                <span className="veri">{k}. kat</span>
                <span className="veri kot">{kot}</span>
                <span className="veri">{list.length - sold}/{list.length} müsait</span>
              </button>
            );
          })}
        </div>
      )}

      {/* KADEME 3: DAİRE (kat planı üzerinde poligon) */}
      {kademe === "daire" && (
        <div className="bel" data-testid="kademe-daire-panel">
          {floorplan?.image && (floorplan.polygons || []).length > 0 ? (
            <div className="poligon-sarici">
              <img src={asset(floorplan.image)} alt={`${seciliBlok} ${seciliKat}. kat planı`} />
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="poligon-svg">
                {(floorplan.polygons || []).map((pg) => {
                  const d = katDaireleri.find((x) => x.no === pg.no);
                  if (!d) return null;
                  const soluk = !uygun(d);
                  const tik = d.durum !== "satildi";
                  const bb = bbox(pg.points);
                  return (
                    <g key={pg.no} opacity={soluk ? 0.25 : 1}>
                      <polygon points={pointsStr(pg.points)} className={`pg-daire pg-${d.durum}`}
                        tabIndex={tik ? 0 : -1} role={tik ? "button" : undefined}
                        aria-label={`Daire ${d.no}, ${DURUM_METIN[d.durum]}, ${d.oda}, ${d.m2} m²`}
                        onClick={() => tik && setSecili(d)} onKeyDown={(e) => tik && e.key === "Enter" && setSecili(d)}
                        data-testid={`poly-daire-${d.no}`} />
                      {d.durum === "satildi" && (<><line x1={bb.x1} y1={bb.y1} x2={bb.x2} y2={bb.y2} className="pg-capraz" /><line x1={bb.x1} y1={bb.y2} x2={bb.x2} y2={bb.y1} className="pg-capraz" /></>)}
                    </g>
                  );
                })}
              </svg>
            </div>
          ) : (
            <div className="daire-kutular">
              {katDaireleri.map((d) => {
                const tik = d.durum !== "satildi";
                return (
                  <button key={d.id} className={`daire-kutu durum-${d.durum} ${!uygun(d) ? "soluk" : ""}`} disabled={!tik}
                    onClick={() => tik && setSecili(d)} data-testid={`daire-kutu-${d.no}`}>
                    <b>{d.no}</b><span className="veri">{d.oda} · {d.m2} m²</span>
                    <span className={`durum-nokta durum-${d.durum}`}><b />{DURUM_METIN[d.durum]}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Filtreler */}
      <div className="daire-arac bel" style={{ marginTop: 32 }}>
        <select value={f.oda} onChange={(e) => setF({ ...f, oda: e.target.value })} data-testid="filtre-oda" aria-label="Oda"><option value="">Tüm odalar</option>{odalar.map((o) => <option key={o}>{o}</option>)}</select>
        <select value={f.cephe} onChange={(e) => setF({ ...f, cephe: e.target.value })} data-testid="filtre-cephe" aria-label="Cephe"><option value="">Tüm cepheler</option>{cepheler.map((o) => <option key={o}>{o}</option>)}</select>
        <input type="number" placeholder="Maks. m²" value={f.maxM2} onChange={(e) => setF({ ...f, maxM2: e.target.value })} data-testid="filtre-m2" />
        <input type="number" placeholder="Maks. fiyat ₺" value={f.maxFiyat} onChange={(e) => setF({ ...f, maxFiyat: e.target.value })} data-testid="filtre-fiyat" />
        <label className="durum-nokta"><input type="checkbox" checked={f.sadeceMusait} onChange={(e) => setF({ ...f, sadeceMusait: e.target.checked })} data-testid="filtre-musait" /> Sadece müsait</label>
      </div>

      {/* Tablo görünümü — erişilebilir + SEO */}
      <div style={{ overflowX: "auto" }}>
        <table className="daire-tablo bel" data-testid="daire-tablo">
          <caption className="veri" style={{ textAlign: "left", marginBottom: 10 }}>Tüm daireler (erişilebilir liste)</caption>
          <thead><tr><th>Daire</th><th>Blok</th><th>Kat / Kot</th><th>Oda</th><th>m²</th><th>Cephe</th><th>Manzara</th><th>Fiyat</th><th>Durum</th></tr></thead>
          <tbody>
            {daireler.map((d) => {
              const soluk = !uygun(d);
              const secilebilir = d.durum !== "satildi";
              return (
                <tr key={d.id} className={soluk ? "soluk" : ""} role={secilebilir ? "button" : undefined} tabIndex={secilebilir ? 0 : undefined}
                  onClick={() => secilebilir && setSecili(d)}
                  onKeyDown={(e) => { if (secilebilir && e.key === "Enter") setSecili(d); }} data-testid={`daire-${d.no}`}>
                  <td className="sayi">{d.no}</td><td>{d.blok}</td><td className="sayi">{d.kat}. / {d.kot}</td>
                  <td>{d.oda}</td><td className="sayi">{d.m2}</td><td>{d.cephe}</td><td>{d.manzara}</td>
                  <td className="sayi">{d.fiyat.toLocaleString("tr-TR")} ₺</td>
                  <td><span className={`durum-nokta durum-${d.durum}`}><b />{DURUM_METIN[d.durum]}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {karsilastir.length > 0 && (
        <div style={{ marginTop: 24 }} data-testid="karsilastir-tepsi">
          <div className="a-satir" style={{ justifyContent: "space-between" }}>
            <h3 className="veri">Karşılaştırma ({karsilastir.length}/3)</h3>
            <div className="a-satir"><button className="a-btn kucuk hayalet" onClick={() => window.print()}>Yazdır</button><button className="a-btn kucuk hayalet" onClick={() => setKarsilastir([])}>Temizle</button></div>
          </div>
          <table className="daire-tablo" style={{ marginTop: 10 }}>
            <thead><tr><th>Daire</th>{karsilastir.map((d) => <th key={d.id}>{d.no}</th>)}</tr></thead>
            <tbody>
              {[["Oda", "oda"], ["m²", "m2"], ["Cephe", "cephe"], ["Manzara", "manzara"], ["Kot", "kot"]].map(([l, k]) => (
                <tr key={k}><td>{l}</td>{karsilastir.map((d) => <td key={d.id} className="sayi">{d[k]}</td>)}</tr>
              ))}
              <tr><td>Fiyat</td>{karsilastir.map((d) => <td key={d.id} className="sayi">{d.fiyat.toLocaleString("tr-TR")} ₺</td>)}</tr>
            </tbody>
          </table>
        </div>
      )}

      <div className={`daire-panel ${secili ? "acik" : ""}`} data-testid="daire-panel">
        {secili && (
          <>
            <div className="a-satir" style={{ justifyContent: "space-between" }}>
              <h3 className="h-orta">Daire {secili.no}</h3>
              <button onClick={() => setSecili(null)} className="veri" data-testid="daire-panel-kapat">Kapat ✕</button>
            </div>
            {floorplan?.image && <img src={asset(floorplan.image)} alt="Kat planı" style={{ margin: "16px 0", border: "1px solid var(--cizgi)" }} />}
            <div style={{ marginTop: 8 }}>
              <div className="kv"><span>Durum</span><b>{DURUM_METIN[secili.durum]}</b></div>
              <div className="kv"><span>Oda</span><b>{secili.oda}</b></div>
              <div className="kv"><span>m²</span><b>{secili.m2}</b></div>
              <div className="kv"><span>Cephe</span><b>{secili.cephe}</b></div>
              <div className="kv"><span>Manzara</span><b>{secili.manzara}</b></div>
              <div className="kv"><span>Kat / Kot</span><b>{secili.kat}. / {secili.kot}</b></div>
              <div className="kv"><span>Fiyat</span><b>{secili.fiyat.toLocaleString("tr-TR")} ₺</b></div>
            </div>
            <div style={{ marginTop: 24, display: "grid", gap: 10 }}>
              <Link to="/randevu" className="dugme" data-testid="daire-teklif">Fiyat teklifi al</Link>
              <button className="dugme hayalet" onClick={() => karsilastirEkle(secili)} data-testid="daire-karsilastir">Karşılaştırmaya ekle</button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* ---------------- Şartname ---------------- */
function Sartname({ specs = [] }) {
  const [acik, setAcik] = useState(0);
  if (!specs.length) return null;
  return (
    <section>
      <div className="bolum-basi"><h2 className="h-orta bel">Teknik şartname</h2></div>
      <div className="sartname bel">
        {specs.map((s, i) => (
          <div className="satir" key={i}>
            <button className="baslik" onClick={() => setAcik(acik === i ? -1 : i)} data-testid={`sartname-${i}`}>
              {s.baslik} <ChevronDown size={20} style={{ transform: acik === i ? "rotate(180deg)" : "none", transition: "transform .3s" }} />
            </button>
            {acik === i && <ul className="icerik">{s.maddeler.map((m, j) => <li key={j}>{m}</li>)}</ul>}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Ana Sayfa ---------------- */
export default function ProjeDetay() {
  const { slug } = useParams();
  const { data: p, isLoading, isError } = useQuery({ queryKey: ["project", slug], queryFn: async () => (await api.get(`/projects/${slug}`)).data, retry: false });
  const [lightbox, setLightbox] = useState(-1);
  useReveal(!!p);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox < 0 || !p) return;
      if (e.key === "Escape") setLightbox(-1);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % p.gallery.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + p.gallery.length) % p.gallery.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, p]);

  if (isLoading) return <Layout><section className="sayfa-basi"><p className="veri">Yükleniyor…</p></section></Layout>;
  if (isError || !p) return <Layout><section className="sayfa-basi"><h1 className="h-orta">Proje bulunamadı</h1><Link to="/projeler" className="dugme" style={{ marginTop: 20 }}>Projelere dön</Link></section></Layout>;

  const devam = p.status === "devam-eden";
  const altNav = [
    { id: "genel", l: "Genel" },
    ...(p.has_apartments ? [{ id: "daireler", l: "Daireler" }] : []),
    ...(devam ? [{ id: "ilerleme", l: "İlerleme" }] : []),
    { id: "konum", l: "Konum" },
    ...(p.payment_plans?.length ? [{ id: "odeme", l: "Ödeme" }] : []),
  ];

  const ld = {
    "@context": "https://schema.org", "@type": "RealEstateListing",
    name: p.name, description: p.intro, url: window.location.href,
    image: asset(p.hero_gunduz),
    address: { "@type": "PostalAddress", addressLocality: p.location, addressCountry: "TR" },
    about: { "@type": "Residence", name: p.name, numberOfRooms: p.kunye?.daire_sayisi },
  };
  const breadcrumb = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: window.location.origin },
      { "@type": "ListItem", position: 2, name: "Projeler", item: `${window.location.origin}/projeler` },
      { "@type": "ListItem", position: 3, name: p.name, item: window.location.href },
    ],
  };
  const faq = (p.specs || []).length ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: p.specs.map((s) => ({ "@type": "Question", name: s.baslik, acceptedAnswer: { "@type": "Answer", text: (s.maddeler || []).join(", ") } })),
  } : null;

  return (
    <Layout mesaj={`${p.name} hakkında bilgi almak istiyorum`}>
      <Meta title={`${p.name} — Özalp İnşaat`} description={(p.intro || "").slice(0, 155)} />
      <JsonLd id="listing" data={ld} />
      <JsonLd id="breadcrumb" data={breadcrumb} />
      {faq && <JsonLd id="faq" data={faq} />}
      {/* HERO */}
      <div className="hero" data-testid="proje-hero">
        <img src={asset(p.hero_gunduz)} alt={p.name} />
        <img className="heroGece" src={asset(p.hero_gece || p.hero_gunduz)} alt={`${p.name} gece`} />
        <div className="hero-ic">
          <div className="veri">{p.location} — {devam ? "İnşaat sürüyor" : "Teslim edildi"}</div>
          <h1 className="h-dev">{p.name}</h1>
        </div>
      </div>

      {/* ALT GEZİNTİ */}
      <div className="alt-gez" data-testid="proje-alt-gezinti">
        {altNav.map((n) => <a key={n.id} href={`#${n.id}`} data-testid={`altnav-${n.id}`}>{n.l}</a>)}
      </div>

      {/* KÜNYE */}
      <div style={{ padding: "0 var(--kenar)" }}>
        <div className="kunye" id="genel">
          <div><div className="veri">Blok</div><b>{p.kunye?.blok}</b></div>
          <div><div className="veri">Kat</div><b>{p.kunye?.kat}</b></div>
          <div><div className="veri">Daire</div><b>{p.kunye?.daire_sayisi}</b></div>
          <div><div className="veri">Teslim</div><b>{p.kunye?.teslim}</b></div>
          <div><div className="veri">m²</div><b>{p.kunye?.m2}</b></div>
        </div>
      </div>

      {/* TANITIM + GALERİ */}
      <section>
        <div className="manifesto" style={{ padding: 0, marginBottom: 44 }}><p className="bel" style={{ fontSize: "clamp(18px,2.2vw,28px)", maxWidth: "34ch" }}>{p.intro}</p></div>
        <div className="galeri bel">
          {(p.gallery || []).map((g, i) => (
            <figure key={i} onClick={() => setLightbox(i)} data-testid={`galeri-${i}`}><img src={asset(g)} alt={`${p.name} ${i + 1}`} loading="lazy" /></figure>
          ))}
        </div>
      </section>

      {/* DAİRE */}
      {p.has_apartments && <DaireSecim slug={slug} p={p} />}

      {/* ZAMAN TÜNELİ */}
      {devam && <ZamanTuneli slug={slug} />}

      {/* KAT MANZARA */}
      {p.has_floor_view && <KatManzara slug={slug} />}

      {/* KONUM */}
      <section id="konum">
        <div className="konum">
          <figure className="bel">{p.map_embed ? <iframe title="Harita" src={p.map_embed} loading="lazy" /> : <img src={asset(p.hero_gunduz)} alt="Konum" />}</figure>
          <div className="bel">
            <div className="veri">Konum</div>
            <h2 className="h-orta" style={{ margin: "12px 0 22px" }}>{p.location}</h2>
            {(p.distances || []).map((d) => <div className="mesafe" key={d.label}><span>{d.label}</span><b>{d.value}</b></div>)}
          </div>
        </div>
      </section>

      {/* ŞARTNAME */}
      <Sartname specs={p.specs} />

      {/* ÖDEME */}
      {p.payment_plans?.length > 0 && (
        <section id="odeme">
          <div className="bolum-basi"><h2 className="h-orta bel">Ödeme planları</h2></div>
          <div className="odeme bel">
            {p.payment_plans.map((o, i) => (<div key={i}><h4>{o.baslik}</h4><p style={{ color: "var(--metin-2)", fontSize: 14 }}>{o.detay}</p></div>))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="kapanis">
        <h2 className="h-orta bel">Yerinde görün</h2>
        <p className="bel">Satış ofisimizde randevu oluşturun.</p>
        <Link to="/randevu" className="dugme bel" data-testid="proje-randevu-btn">Randevu oluştur</Link>
      </section>

      {/* LIGHTBOX */}
      {lightbox >= 0 && (
        <div className="lightbox" onClick={() => setLightbox(-1)} data-testid="lightbox">
          <button className="kapat veri" onClick={() => setLightbox(-1)}>ESC ✕</button>
          <button className="ok sol veri" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + p.gallery.length) % p.gallery.length); }}>←</button>
          <img src={asset(p.gallery[lightbox])} alt="Görsel" onClick={(e) => e.stopPropagation()} />
          <button className="ok sag veri" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % p.gallery.length); }}>→</button>
        </div>
      )}
    </Layout>
  );
}
